import client from './client';

/** 卖出候选条目 */
export interface SellItem {
  code: string;
  name: string;
  buyPrice: number;
  quantity: number;
  currentPrice: number;
  intradayHigh: number;
  intradayLow: number;
  takeProfitPrice: number;
  /** 止损线价格；null = 未启用任何止损口径 */
  stopLossPrice: number | null;
  /** 止损口径来源（2026-09-17 新增） */
  stopLossKind: '30d_low' | 'loss_threshold' | 'both' | 'none';
  /** loss_30d_low_stop_days 的 N；null = 未启用 */
  stopLossDays: number | null;
  /** 止损阈值（百分点）；0 = 未启用 */
  profitThreshold: number;
  lossThreshold: number;
  highPct: number;
  lowPct: number;
  status: 'triggered_profit' | 'triggered_loss' | 'hold';
  reason: string;
}

/** 买入条目 — 「上一筛选 → 今日 10:00 尝试买入」 */
export interface BuyItem {
  code: string;
  name: string;
  priceAt10am: number;
  currentPrice: number;
  plannedAmount: number;
  plannedQuantity: number;
  actualAmount: number;
  traded: boolean;
  blockedBy: string | null;
  matchedRules: string;
}

/** 待购条目 — 「今日筛选 → 次日 10:00 待购」 */
export interface PendingItem {
  code: string;
  name: string;
  screenedPrice: number;
  currentPrice: number;
  plannedAmount: number;
  plannedQuantity: number;
  matchedRules: string;
}

/** 买入区数据块（上一筛选日的成果 + 今日 10:00 成交对比） */
export interface BuyBrief {
  list: BuyItem[];
  candidateCount: number;
  tradedCount: number;
  blockedCount: number;
  plannedAmount: number;
  candidateDate: string;
  source: string;
}

/** 待购区数据块（今日收盘筛选 → 次日 10:00 买入） */
export interface PendingBrief {
  list: PendingItem[];
  candidateCount: number;
  plannedAmount: number;
  candidateDate: string;
  source: string;
}

/** 今日成交条目 */
export interface TodayTradeItem {
  id: number;
  code: string;
  name: string;
  direction: 'buy' | 'sell';
  price: number;
  quantity: number;
  amount: number;
  profit: number;
  profitPct: number;
  reason: string;
  tradeTime: string;
}

/** 全局门控 */
export interface GlobalLock {
  locked: boolean;
  reasons: string[];
  triggeredAt: string | null;
}

/** 自检 */
export interface SelfCheckBrief {
  checkTime: string;
  status: 'ok' | 'fixed' | 'error';
  issuesFound: number;
  issuesFixed: number;
  durationMs: number;
  issues: any[];
}

/** 当日盈亏 */
export interface DailyPnlBrief {
  realizedProfit: number;
  unrealizedProfit: number;
  totalAssets: number;
  balance: number;
  buyCount: number;
  sellCount: number;
  heldCount: number;
}

/** 挂起补买 */
export interface PendingBuyItem {
  code: string;
  name: string;
  status: string;
  error: string;
}

/** 今日简报 */
export interface TodayBrief {
  tradeDate: string;
  generatedAt: string;
  market: {
    upCount: number;
    downCount: number;
    flatCount: number;
    shIndex: number;
    shChangePct: number;
    szIndex: number;
    szChangePct: number;
  };
  sell: {
    list: SellItem[];
    triggerCount: number;
    holdCount: number;
    profitThreshold: number;
    /** 止损阈值（百分点）；0 = 未启用（2026-09-17 起不再默认 5） */
    lossThreshold: number;
    /** loss_30d_low_stop_days 的 N；null = 未启用 */
    stopDays: number | null;
    /** 是否启用了任一止损口径 */
    stopLossEnabled: boolean;
  };
  buy: BuyBrief;
  pending: PendingBrief;
  todayTrades: TodayTradeItem[];
  globalLock: GlobalLock;
  selfCheck: SelfCheckBrief | null;
  dailyPnl: DailyPnlBrief | null;
  pendingBuys: PendingBuyItem[];
}

/** 归档日期列表项 */
export interface ArchivedDateItem {
  date: string;
  archivedAt: string;
  selfCheckStatus: string;
  issuesFound: number;
  rejected: boolean;
}

/** 归档内容（嵌套一份 TodayBrief + 摘要） */
export interface ArchivedBriefResult {
  date: string;
  archivedAt: string;
  selfCheckStatus: string;
  issuesFound: number;
  issuesFixed: number;
  warnings: any[];
  brief: TodayBrief | null;
  rejected: boolean;
}

/** 获取今日简报 */
export async function fetchTodayBrief(): Promise<TodayBrief> {
  const res = await client.get<TodayBrief>('/today-brief');
  return res.data;
}

/** 获取所有归档日期列表 */
export async function fetchArchivedDates(): Promise<ArchivedDateItem[]> {
  const res = await client.get<ArchivedDateItem[]>('/daily-brief/dates');
  return res.data;
}

/** 获取指定日期归档内容 */
export async function fetchArchivedBrief(date: string): Promise<ArchivedBriefResult> {
  const res = await client.get<ArchivedBriefResult>('/daily-brief', { params: { date } });
  return res.data;
}

/** 手动触发归档 */
export async function triggerArchive(): Promise<{
  archived: boolean;
  date: string;
  reason: string;
  selfCheckStatus: string;
  issuesFound: number;
  issuesFixed: number;
}> {
  const res = await client.post<{
    archived: boolean;
    date: string;
    reason: string;
    selfCheckStatus: string;
    issuesFound: number;
    issuesFixed: number;
  }>('/daily-brief/archive');
  return res.data;
}
